/**
 * Homepage Configuration
 * Controls feature flags and A/B testing for homepage components
 */

export const homepageConfig = {
  // Toggle between data-driven insights (v1) and success stories (v2)
  useSuccessStoryFormat: true, // Set to false to use old format
  
  // Success story social proof numbers
  socialProof: {
    restaurantsThisWeek: 147,
    revenueGeneratedMillions: 2.3,
    averageGrowthPercentage: '15-20', // Updated to range
    totalRestaurants: 500,
    averageRating: 4.9
  },
  
  // A/B Testing configuration (if needed)
  abTesting: {
    enabled: false,
    testName: 'homepage-success-stories',
    variants: {
      control: 'LiveInsightsSection',
      treatment: 'LiveInsightsSectionV2'
    }
  },
  
  // Feature flags
  features: {
    showSocialProofBar: true,
    showTrustIndicators: true,
    showUrgencyMessage: true,
    animateBackgrounds: true,
    showRevenueCalculator: false // Disabled in V2
  },
  
  // CTA Configuration
  cta: {
    mainButtonText: 'START YOUR FREE TRIAL',
    secondaryButtonText: 'Watch Live Demo',
    urgencyMessage: '🎯 Limited time: Get 2-3 months FREE for all annual plans with 20% discount'
  }
};

// Success stories data - could be moved to a database/CMS later
export const successStoriesData = [
  {
    id: 'virtual-host-story',
    type: 'phone' as const,
    restaurantName: "Bella Pizza",
    ownerName: "Tony Russo",
    location: "Detroit",
    beforeMetric: "47",
    afterMetric: "Zero",
    metricLabel: "Missed Calls / Week",
    headline: "Never Miss an Order Again",
    quote: "We were losing $800/week in missed calls during rush hour. Now our AI host answers every call instantly and takes orders perfectly",
    impact: "$3,200 additional revenue per month",
    actionLabel: "Stop Missing Calls",
    featured: true
  },
  {
    id: 'revenue-story',
    type: 'revenue' as const,
    restaurantName: "Marco's Italian Kitchen",
    ownerName: "Marco DiAngelo",
    location: "NYC",
    beforeMetric: "$2,400",
    afterMetric: "$3,100",
    metricLabel: "Daily Revenue Increase",
    headline: "From Struggling to Thriving",
    quote: "AI found that our Tuesday special was cannibalizing our profit makers. One menu adjustment = $700 more per day",
    impact: "$21,000 additional revenue per month",
    actionLabel: "See How It Works",
    featured: true
  },
  {
    id: 'competition-story',
    type: 'competition' as const,
    restaurantName: "Bella's Bistro",
    ownerName: "Isabella Rodriguez",
    location: "Chicago",
    beforeMetric: "#6",
    afterMetric: "#1",
    metricLabel: "Neighborhood Ranking",
    headline: "From Surviving to Winning",
    quote: "We're beating the chains! AI shows us exactly what our customers want. Our ratings jumped from 3.8 to 4.7 stars",
    impact: "40% increase in 5-star reviews",
    actionLabel: "Start Winning Today",
    featured: true
  },
  {
    id: 'time-story',
    type: 'time' as const,
    restaurantName: "The Rustic Table",
    ownerName: "Sarah Mitchell",
    location: "Austin",
    beforeMetric: "3 staff",
    afterMetric: "24/7",
    metricLabel: "Virtual Assistant Coverage",
    headline: "Never Short-Staffed Again",
    quote: "Our AI assistant handles 80% of customer questions instantly. It's like having our best server working every shift, never calls in sick",
    impact: "Zero missed opportunities during peak hours",
    actionLabel: "Get Your Time Back",
    featured: false
  },
  // Additional stories for rotation/testing
  {
    id: 'customer-story',
    type: 'competition' as const,
    restaurantName: "Pacific Grill",
    ownerName: "David Chen",
    location: "Seattle",
    beforeMetric: "3.5",
    afterMetric: "4.8",
    metricLabel: "Google Rating",
    headline: "Customer Satisfaction Soared",
    quote: "AI helped us identify and fix service bottlenecks. Our customer complaints dropped 67% in just 2 months",
    impact: "67% fewer complaints",
    actionLabel: "Delight Your Customers",
    featured: false
  },
  {
    id: 'efficiency-story',
    type: 'time' as const,
    restaurantName: "The Corner Cafe",
    ownerName: "Maria Santos",
    location: "Miami",
    beforeMetric: "60%",
    afterMetric: "95%",
    metricLabel: "Order Accuracy",
    headline: "Eliminated Order Mistakes",
    quote: "Our AI assistant handles all the complex dietary questions. Staff can focus on service, not memorizing ingredients",
    impact: "35% improvement in accuracy",
    actionLabel: "Streamline Operations",
    featured: false
  },
  {
    id: 'growth-story',
    type: 'revenue' as const,
    restaurantName: "Downtown Deli",
    ownerName: "Tom Williams",
    location: "Boston",
    beforeMetric: "150",
    afterMetric: "230",
    metricLabel: "Daily Customers",
    headline: "53% More Customers",
    quote: "AI-powered recommendations increased our average ticket by $8. Same customers, much better margins",
    impact: "80 new customers daily",
    actionLabel: "Grow Your Business",
    featured: false
  }
];

// Export function to get featured stories
export const getFeaturedStories = () => {
  return successStoriesData.filter(story => story.featured);
};

// Export function to get random stories for A/B testing
export const getRandomStories = (count: number = 3) => {
  const shuffled = [...successStoriesData].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
};